import { useState, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Loader2, Printer } from "lucide-react";
import { useUserFilter, userFilterSearch } from "@/lib/user-filter";
import { UserFilterChip } from "@/components/admin/user-filter-chip";
import { apiGet } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/admin/signing-reports")({
  component: SigningReportsPage,
  validateSearch: userFilterSearch,
});

interface SigningRow {
  ackId: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  divisionName: string | null;
  documentId: string;
  docTitle: string | null;
  categoryName: string | null;
  versionNumber: number | null;
  signedAt: string | null;
  approvedAt: string | null;
  status: string;
}

interface FilterDoc { id: string; title: string }
interface FilterDiv { id: string; name: string }
interface FilterCat { id: string; name: string }

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export default function SigningReportsPage() {
  const tableRef = useRef<HTMLDivElement>(null);
  const userFilter = useUserFilter();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedDoc, setSelectedDoc] = useState("all");
  const [selectedDiv, setSelectedDiv] = useState("all");
  const [selectedCat, setSelectedCat] = useState("all");

  const { data: docsData } = useQuery({
    queryKey: ["admin", "docs-list-filter"],
    queryFn: () =>
      apiGet<{ data: { doc: FilterDoc }[] }>("/api/admin/safety-docs?status=active").then((r) => ({
        data: r.data.map((x) => x.doc),
      })),
  });
  const { data: divsData } = useQuery({
    queryKey: ["admin", "divisions-filter"],
    queryFn: () => apiGet<{ data: FilterDiv[] }>("/api/admin/divisions"),
  });
  const { data: catsData } = useQuery({
    queryKey: ["admin", "cats-filter"],
    queryFn: () => apiGet<{ data: FilterCat[] }>("/api/admin/safety-docs/categories"),
  });

  const params = new URLSearchParams();
  if (dateFrom) params.set("from", dateFrom);
  if (dateTo) params.set("to", dateTo);
  if (selectedDoc !== "all") params.set("documentId", selectedDoc);
  if (selectedDiv !== "all") params.set("divisionId", selectedDiv);
  if (selectedCat !== "all") params.set("categoryId", selectedCat);
  if (userFilter.userId) params.set("userId", userFilter.userId);

  const { data: reportData, isLoading } = useQuery({
    queryKey: ["admin", "signing-report", dateFrom, dateTo, selectedDoc, selectedDiv, selectedCat, userFilter.userId],
    queryFn: () => apiGet<{ data: SigningRow[]; total: number }>(`/api/admin/doc-assignments/reports/signing?${params}`),
  });

  const rows = reportData?.data ?? [];

  function handlePrint() {
    window.print();
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 pb-16">
      <div className="print:hidden">
        {userFilter.userId && <UserFilterChip name={userFilter.userName} onClear={userFilter.clear} />}
      </div>
      {/* Page header — hidden when printing */}
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-lg font-bold">Signing Reports</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5">
            <Printer className="h-4 w-4" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Filters — hidden when printing */}
      <div className="flex flex-wrap gap-2 items-end print:hidden">
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-36" />
        </div>
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-36" />
        </div>
        <SearchableSelect
          size="sm"
          className="w-44"
          aria-label="Category"
          value={selectedCat}
          onChange={setSelectedCat}
          allLabel="All Categories"
          placeholder="All categories"
          options={(catsData?.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <SearchableSelect
          size="sm"
          className="w-56"
          aria-label="Document"
          value={selectedDoc}
          onChange={setSelectedDoc}
          allLabel="All Documents"
          placeholder="All documents"
          searchPlaceholder="Type a document name..."
          options={(docsData?.data ?? []).map((d) => ({ value: d.id, label: d.title }))}
        />
        <SearchableSelect
          size="sm"
          className="w-44"
          aria-label="Division"
          value={selectedDiv}
          onChange={setSelectedDiv}
          allLabel="All Divisions"
          placeholder="All divisions"
          options={(divsData?.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
        />
        {(dateFrom || dateTo || selectedDoc !== "all" || selectedDiv !== "all" || selectedCat !== "all") && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => {
            setDateFrom(""); setDateTo(""); setSelectedDoc("all"); setSelectedDiv("all"); setSelectedCat("all");
          }}>Clear</Button>
        )}
      </div>

      {/* Print header — only visible when printing */}
      <div className="hidden print:block mb-6">
        <div className="flex items-center justify-between border-b-2 border-gray-800 pb-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Paris Mechanical</h1>
            <p className="text-sm text-gray-600">PPH Safety — Document Signing Report</p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>Generated: {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</p>
            {(dateFrom || dateTo) && <p>Period: {dateFrom || "—"} to {dateTo || "—"}</p>}
            <p>Total records: {rows.length}</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12 print:hidden"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div ref={tableRef}>
          <p className="text-xs text-muted-foreground mb-2 print:hidden">{rows.length} record{rows.length !== 1 ? "s" : ""}</p>
          <div className="overflow-x-auto rounded-lg border border-border print:border-gray-300">
            <table className="w-full text-xs border-collapse print:text-[10px]">
              <thead>
                <tr className="bg-blue-700 print:bg-[#1d4ed8] text-white">
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">#</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Employee</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Division</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Category</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Document</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Ver.</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-600 print:border-blue-800">Signed</th>
                  <th className="px-3 py-2 text-left font-semibold">Approved</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground print:text-gray-500">
                      No signed documents found for the selected filters
                    </td>
                  </tr>
                ) : rows.map((r, i) => (
                  <tr
                    key={r.ackId}
                    className={
                      i % 2 === 0
                        ? "bg-white print:bg-white dark:bg-card"
                        : "bg-blue-50 print:bg-blue-50 dark:bg-muted/30"
                    }
                  >
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 text-muted-foreground print:text-gray-500 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200">
                      <p className="font-medium text-foreground print:text-gray-900">{r.userName ?? "—"}</p>
                      <p className="text-[10px] text-muted-foreground print:text-gray-500">{r.userEmail ?? ""}</p>
                    </td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 text-foreground print:text-gray-800">{r.divisionName ?? "—"}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 text-foreground print:text-gray-800">{r.categoryName ?? "—"}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 font-medium text-foreground print:text-gray-900">{r.docTitle ?? "—"}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 text-center text-muted-foreground print:text-gray-600">v{String(r.versionNumber ?? 0).padStart(2, "0")}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 text-foreground print:text-gray-800 whitespace-nowrap">{fmtDate(r.signedAt)}</td>
                    <td className="px-3 py-1.5 border border-border print:border-gray-200 whitespace-nowrap">
                      {r.approvedAt
                        ? <span className="text-emerald-700 print:text-emerald-800 font-medium">{fmtDate(r.approvedAt)}</span>
                        : <span className="text-muted-foreground print:text-gray-400">Pending</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print\\:block { display: block !important; visibility: visible; }
          .print\\:hidden { display: none !important; }
          [data-print-area], [data-print-area] * { visibility: visible; }
          table, table * { visibility: visible !important; }
          thead { background-color: #1d4ed8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          tr:nth-child(even) { background-color: #eff6ff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 15mm; size: A4 landscape; }
        }
      `}</style>
    </div>
  );
}
